import { PublicClientApplication } from "@azure/msal-browser";
import { msalLogin, MsalLoginFn, msalRefreshLogin } from "./msal.login";
import { MsalLogout, msalLogout } from "./msal.logout";
import { DebugLog, makeDebugLog } from "@me8eon/debug";
import { msalJwtToken } from "./msal.jwt.token";
import { JwtTokenFn, LoginConfig, LoginOutFn } from "@me8eon/authentication";


/**
 * Creates an MSAL-based login handler.
 * @param msalInstance - The MSAL PublicClientApplication instance.
 * @param scopes - The scopes to request during login. Defaults to ["openid", "profile", "user.read", "offline_access"].
 */
type MsalLoginConfig = {
    msal: PublicClientApplication;
    scopes?: string[];
    _msalLogin?: MsalLoginFn
    _msalLogout?: MsalLogout,
    _msalRefreshLogin?: MsalLoginFn

}

/**
 * Configuration for MSAL-based login handler.
 * @property msal - The MSAL PublicClientApplication instance.
 * @property scopes - The scopes to request during login. Defaults to ["openid", "profile", "user.read", "offline_access"].
 * @property _msalLogin - The login function to use. Defaults to `msalLogin`. Only override if you know what you're doing (i.e. testing)
 * @property _msalLogout - The logout function to use. Defaults to `msalLogout`. Only override if you know what you're doing (i.e. testing)
 */
export function loginUsingMsal({
    msal,
    scopes = ["openid", "profile", "user.read", "offline_access"],
    _msalLogin = msalLogin,
    _msalLogout = msalLogout,
    _msalRefreshLogin = msalRefreshLogin,
}: MsalLoginConfig): LoginConfig {
    const login: LoginOutFn = async (debug: DebugLog) => {
        debug("loginUsingMsal.login");
        const accounts = msal.getAllAccounts();
        const account0 = accounts[0];
        if (account0) msal.setActiveAccount(account0);
        const res = await _msalLogin(msal)(scopes, debug);
        debug("loginUsingMsal.login - ended");
        return res?.idToken;
    };

    const logout: LoginOutFn = async (debug: DebugLog) => {
        const accounts = msal.getAllAccounts();
        const account = accounts[0];
        if (!account) {
            return;
        }
        sessionStorage.clear();
        localStorage.clear();
        await _msalLogout.logout(msal, account);
        debug("loginUsingMsal after logout");
        return undefined;
    };

    const refreshLogin: LoginOutFn = async (debug: DebugLog) => {
        debug("loginUsingMsal.refreshLogin");
        const res = await _msalRefreshLogin(msal)(scopes, debug);
        debug("loginUsingMsal.refreshLogin - ended");
        return res?.idToken;
    };

    const jwtToken: JwtTokenFn = async () => {
        await login(makeDebugLog({}, "login"));
        return msalJwtToken(msal)();
    };

    return { type: "msal", login, logout, refreshLogin, jwtToken };
}


